<script setup>
    import Layout from "@/layouts/main.vue";
    import PageHeader from "@/components/page-header";  
    import useVuelidate from "@vuelidate/core";
    import { required, helpers, maxLength } from "@vuelidate/validators";
    import { computed, ref, reactive, onMounted } from 'vue';
    import { useStore } from 'vuex';

    const state = reactive({
        name: '',
        description: '',
        remarks: '',
        article: '',
        base: false,
        status: false,
        lgu_id: '',
        penalties: [
            { name: '', penalty_fee: null, offense_number: 1, article: '' },
        ]
    });

    const rules = computed(() => ({
        name: {
            required: helpers.withMessage("Violation Name is required", required)
        },
        base: {
            required: helpers.withMessage("Violation Base is required", required),
        },
        status: {
            required: helpers.withMessage("Violation Status is required", required)
        },
        lgu_id: {
            required: helpers.withMessage("LGU is required", required)
        },
        penalties: {
            $each: helpers.forEach({
                name: {
                    required: helpers.withMessage("Penalty name is required", required)
                },
                offense_number: {
                    required: helpers.withMessage("Offense Number is required", required)
                },
                penalty_fee: {
                    required: helpers.withMessage("Penalty Fee is required", required),
                    maxLengthValue: helpers.withMessage("You've reached maximum value for penalty fee", maxLength(8))
                }
            })
        }
    }));

    const store = useStore();
    const v$ = useVuelidate(rules, state);
    const loading = computed(() => store.state.globals.loading);

    const title = ref("Violation Types");
    const items = ref([
        {
            text: "Home",
            href: "/",
        },
        {
            text: "Settings",
            href: "/settings/violation-types/list",
            active: true
        },
        {
            text: "Add New Violation Type",
            active: true,
        }
    ]);

    const lguOptions = computed(() => store ? store.state.lgu.dropdownLGUs : []);
    const submitted = ref(false);
    const notification = computed(() => store.state.notification);

    const createViolationType = () => {
        submitted.value = true
        v$.value.$touch();

        if (v$.value.$invalid) return;
        store.dispatch('violationType/createViolationType', state);
    }

    const getAllLGUs = () => {
        store.dispatch('lgu/getDropdownLGUs')
    }

    const removeOffense = (currentIndex) => {
        const penalties = state.penalties;

        if (currentIndex === 0) return;
        penalties.splice(currentIndex, 1);
        penalties.map((penalty, index) => {
            penalty.offense_number = index + 1;
            return penalty;
        })
    }

    const addOffense = () => {
        state.penalties = [ 
            ...state.penalties, 
            { 
                name: '', 
                offense_number: state.penalties.length + 1, 
                article: null,
                penalty_fee: null 
            }
        ]
    }

    onMounted(() => {
        getAllLGUs();
    })
</script>

<template>
  <Layout>
    <PageHeader back="/settings/violation-types/list" :title="title" :items="items" />
    <div class="row">
        <div class="col lg-12">
            <b-overlay :show="loading">
                <div class="card">
                    <div class="card-header">
                        <h4 class="card-title mb-0">Add New Violation Type</h4>
                    </div>
                    <div class="card-body">
                        <form @submit.prevent="createViolationType" class="mx-2">
                            <div v-if="notification.messages.length > 0">
                                <b-alert v-for="(message, index) in notification.messages" :key="index"
                                    show variant="danger" class="fade show" role="alert">
                                    {{ message }}
                                </b-alert>
                            </div>
                            <div class="row">
                                <p class="fs-6 text text-decoration-underline">Violation Information</p>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="name" class="form-label required">Violation Name</label> 
                                        <input
                                            autofocus
                                            :class="{ 'is-invalid': submitted && v$.name.$error }"
                                            v-model="state.name" type="text" class="form-control" placeholder="Enter violation name..." id="name" />
                                        <div
                                            v-for="item of v$.name.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="description" class="form-label">Violation Description</label> 
                                        <textarea v-model="state.description" class="form-control" placeholder="Enter violation description..." id="description"></textarea>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="remarks" class="form-label">Remarks</label> 
                                        <textarea 
                                            v-model="state.remarks" class="form-control" placeholder="Enter remarks..." id="remarks"></textarea>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="article" class="form-label">Article</label>
                                        <input 
                                            v-model="state.article" type="text" class="form-control" placeholder="Enter article..." id="article" />
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="lguInput" class="form-label required">LGU</label>
                                        <select 
                                            :class="{ 'is-invalid': submitted && v$.lgu_id.$error }"
                                            v-model="state.lgu_id" id="lguInput" class="form-select" aria-label="Select LGU">
                                            <option value="">Select LGU</option>
                                            <option v-for="lgu in lguOptions" :key="lgu.id" :value="lgu.id">{{ lgu.name }}</option>
                                        </select>
                                        <div
                                            v-for="item of v$.lgu_id.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label for="violation-status" class="form-label required">Status</label>
                                        <div class="form-check form-switch form-switch-lg" dir="ltr">
                                            <input type="checkbox" v-model="state.status" class="form-check-input" id="violation-status" checked="">
                                            <label class="form-check-label" for="violation-status">{{ state.status ? 'Active' : 'Inactive' }}</label>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label for="violation-base" class="form-label required">Base</label>
                                        <div class="form-check form-switch form-switch-lg" dir="ltr">
                                            <input type="checkbox" v-model="state.base" class="form-check-input" id="violation-base" checked="">
                                            <label class="form-check-label" for="violation-base">{{ state.base ? 'Yes' : 'No' }}</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="border-bottom mb-4"></div>
                            
                            <div class="row mb-3">
                                <p class="fs-6 text text-decoration-underline">Offenses and Penalties</p>

                                <b-list-group>
                                    <b-list-group-item v-for="(item, index) in state.penalties" :key="index">
                                        <div class="row">
                                            <div class="col-md-2 mb-3">
                                                <label :for="`offense-name-${index}`" class="form-label required">Name</label> 
                                                <input 
                                                    :class="{ 'is-invalid': submitted && v$.penalties.$each.$response.$errors[index].name.length }"
                                                    v-model="item.name" type="text" class="form-control" placeholder="Enter name..." :id="`offense-name-${index}`">
                                                <div
                                                    v-for="item of v$.penalties.$each.$response.$errors[index].name"
                                                    :key="item.$uid"
                                                    class="invalid-feedback"
                                                >
                                                    <span v-if="item.$message">{{ item.$message }}</span>
                                                </div>
                                            </div>
                                            <div class="col-md-2 mb-3">
                                                <label :for="`offense-ordinance-${index}`" class="form-label">Ordinance</label> 
                                                <input
                                                    v-model="item.article" type="text" class="form-control" placeholder="Enter ordinance..." :id="`offense-ordinance-${index}`">
                                            </div>
                                            <div class="col-md-2 mb-3">
                                                <label :for="`offense-offenseNo-${index}`" class="form-label required">Offense No.</label> 
                                                <input
                                                    readonly
                                                    :class="{ 'is-invalid': submitted && v$.penalties.$each.$response.$errors[index].offense_number.length }"
                                                    v-model="item.offense_number" type="number" class="form-control" placeholder="Enter offense number..." :id="`offense-offenseNo-${index}`">
                                                <div
                                                    v-for="item of v$.penalties.$each.$response.$errors[index].offense_number"
                                                    :key="item.$uid"
                                                    class="invalid-feedback"
                                                >
                                                    <span v-if="item.$message">{{ item.$message }}</span>
                                                </div>
                                            </div>
                                            <div class="col-md-3 mb-3">
                                                <label :for="`offense-penalty-${index}`" class="form-label">Penalty Fee</label> 
                                                <input 
                                                    v-maska="[
                                                        '#.##','##.##','###.##','####.##','#####.##'
                                                    ]"
                                                    :class="{ 'is-invalid': submitted && v$.penalties.$each.$response.$errors[index].penalty_fee.length }"
                                                    v-model="item.penalty_fee" type="text" class="form-control" placeholder="Enter penalty..." :id="`offense-penalty-${index}`">
                                                 <div
                                                    v-for="item of v$.penalties.$each.$response.$errors[index].penalty_fee"
                                                    :key="item.$uid"
                                                    class="invalid-feedback"
                                                >
                                                    <span v-if="item.$message">{{ item.$message }}</span>
                                                </div>
                                            </div>
                                            <div class="col-auto mb-3 d-flex">
                                                <button @click="removeOffense(index)" :disabled="index === 0" class="btn btn-warning align-self-center mr-2" type="button"><i class="mdi mdi-delete fs-5 mr-1 valign-middle"></i> Remove</button>
                                                <button 
                                                    v-show="index === state.penalties.length - 1"
                                                    @click="addOffense(index)" 
                                                    class="btn btn-success align-self-center"
                                                    type="button">
                                                        <i class="mdi mdi-plus fs-5 mr-1 valign-middle"></i> Add more offense
                                                </button>
                                            </div>
                                        </div>
                                    </b-list-group-item>
                                </b-list-group>
                            </div>

                            <div class="row">
                                <div class="col-lg-12">
                                    <div class="text-end">
                                        <router-link to="/settings/violation-types/list" type="submit" class="btn btn-danger mr-3">
                                            <i class="ri-close-fill fs-4 mr-1 valign-middle"></i>
                                            Cancel
                                        </router-link>
                                        <button type="submit" class="btn btn-success">
                                            <i class="ri-save-2-fill fs-4 mr-1 valign-middle"></i>
                                            Add Violation Type
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </b-overlay>
        </div>
    </div>
  </Layout>
</template>